import React, { createContext, useContext, useState, PropsWithChildren, useCallback, ReactNode } from 'react';
import {
	DialogRoot,
	DialogContent
} from '../chakra-components/Dialog';
import { Events, Result } from 'core/infrastructure';
import { DialogContentProps, DialogRootProps } from '@chakra-ui/react';
import * as ModalUtils from './ModalUtils';
import { WaitingOverlayProvider } from './WaitingOverlay';
import { ModalContext, DismissHandler } from './ModalHooks';

type Options = {
	content?: ReactNode;
	size?: 'auto' | DialogRootProps['size'];
	closeOnInteractOutside?: boolean;
	placement?: DialogRootProps['placement'];
}

type Instance<T = unknown> = Options & {
	modalId: string
	resolve: (value: T) => void;
	closeResult?: Result<T>;
	open?: boolean;
	deferredClose?: boolean;
	dismissHandler?: DismissHandler;
}

type ContextType = {
	show: <T, >(instance: Omit<Instance, 'resolve'>) => Promise<Result<T>>
	close: <T, >(modalId: string, result: T) => void;
	cancel: (modalId: string) => void;
	setDismissHandler: (modalId: string, handler: DismissHandler) => void;
}

const Context = createContext<ContextType | undefined>(undefined);
const IsCoveredByOtherScreenContext = createContext(false);

export const useIsCoveredByOtherScreen = () => useContext(IsCoveredByOtherScreenContext);

let nextModalId = 0;

const useBuilder = () => {

	const dialogContext = useContext(Context);
	if (!dialogContext) {
		throw new Error('useBuilder debe ser usado dentro de Modal.Provider');
	}

	return function () {
		const modalId = `dialog-${++nextModalId}`;

		return {
			show: <T,>(options: Options) => dialogContext.show<T>({ ...options, modalId }),
			close: <T,>(result: T) => dialogContext.close<T>(modalId, result),
			cancel: () => dialogContext.cancel(modalId)
		};
	};
};


type ModalProps = {
	modal: Instance;
	cancel: (modalId: string) => void;
	onExitComplete: (modalId: string) => void;
	isTop: boolean;
	isFullScreen: boolean;
	isCoveredByOtherScreen: boolean;
	setDismissHandler: (modalId: string, handler: DismissHandler) => void;
	onFullScreenChange: (modalId: string, isFullScreen: boolean) => void;
}

const Modal = React.memo(({ modal, onExitComplete, cancel, isTop, isFullScreen, isCoveredByOtherScreen, setDismissHandler, onFullScreenChange }: ModalProps) => {

	const contentRef = React.useRef<HTMLDivElement>(null as unknown as HTMLDivElement);
	const setModalDismissHandler = React.useCallback((handler: DismissHandler) => setDismissHandler(modal.modalId, handler), [modal.modalId, setDismissHandler]);

	const placement = modal.placement || 'center';
	const variant = modal.size || 'auto';

	const isContentFullScreen = useCallback((element: HTMLElement) => {
		// Si el contenido mide al menos lo mismo que la ventana en alto y ancho, asumimos que es fullscreen
		return element.offsetHeight >= window.innerHeight && element.offsetWidth >= window.innerWidth;
	}, []);

	// Comprobamos si estamos en FullScreen para evitar poner borders redondeados al dialogo. No podemos hacerlo en base al size, porque por defecto
	// son auto y crecen al tamaño que vamos definiendo en las pantallas o desde PosScreen. Salvo que pasemos un valor.
	React.useEffect(() => {
		if (contentRef.current)
			onFullScreenChange(modal.modalId, isContentFullScreen(contentRef.current));
	}, [isContentFullScreen, modal.modalId, onFullScreenChange]);

	const onCloseComplete = React.useCallback(() => onExitComplete(modal.modalId), [onExitComplete, modal.modalId]);

	const isEventInsideContent = useCallback((event: PointerEvent, contentElement: HTMLElement) => {
		return event.target instanceof Node && contentElement.contains(event.target);
	}, []);

	const canCloseFromOutside = useCallback(() => {
		return !isFullScreen && modal.closeOnInteractOutside !== false;
	}, [isFullScreen, modal.closeOnInteractOutside]);

	const requestDismiss = useCallback(async () => {
		if (modal.dismissHandler) {
			await modal.dismissHandler();
			return;
		}

		cancel(modal.modalId);
	}, [cancel, modal]);

	const requestDismissFromOutside = useCallback(async () => {
		if (!canCloseFromOutside())
			return;

		await requestDismiss();
	}, [canCloseFromOutside, requestDismiss]);

	Events.useEventOn('browser:back', async ({ viewId }) => {
		if (modal.modalId === viewId && isTop && modal.open)
			await requestDismiss();
	});

	const context = React.useMemo(() => ({
		contentRef,
		isOnTop: isTop,
		setDismissHandler: setModalDismissHandler
	}), [contentRef, isTop, setModalDismissHandler]);

	React.useEffect(() => {
		// Si no soy el modal onTop o ya no estoy abierto, no registro onPointerDown
		if (!isTop || !modal.open)
			return;

		const onPointerDown = async (event: PointerEvent) => {
			const contentElement = contentRef.current;
			if (!contentElement)
				return;

			// Si el click es dentro del modal no hacemos nada
			if (isEventInsideContent(event, contentElement))
				return;

			event.stopPropagation();
			event.preventDefault();

			await requestDismissFromOutside();
		};

		document.addEventListener('pointerdown', onPointerDown, true);
		return () => document.removeEventListener('pointerdown', onPointerDown, true);

	}, [isEventInsideContent, isTop, modal.open, requestDismissFromOutside]);

	// Aunque por defecto DialogRoot tiene size='md', eso lo que hace es establecer un maxW/maxH para el content. Nosotros
	// queremos que nuestro contenido marque el tamaño del dialogo.
	const rootSize: DialogRootProps['size'] = React.useMemo(() => variant === 'auto' ? undefined : variant, [variant]);
	const contentSizeProps: DialogContentProps | undefined = React.useMemo(() => variant === 'auto' ? ({ width: 'auto', height: 'auto', maxWidth: '100dvw', maxHeight: '100dvh' }) : undefined, [variant]);

	return <ModalContext.Provider value={context}>

		{/*
			La decisión importante es que sólo puede haber un dueño del comportamiento modal. Chakra Dialog usa Zag por debajo y,
			si lo dejamos con sus defaults, Zag asume que ese dueño es él: crea su propio modelo modal, atrapa/restaura foco,
			bloquea scroll, oculta contenido con aria-hidden, controla pointer-events y decide cierres por outside/Escape.

			Nuestro caso no es un dialog aislado. ModalProvider es una API de pantallas async: mantiene un stack propio, permite
			cerrar pantallas que no están arriba con deferredClose, espera a onExitComplete para resolver show(), restaura foco y
			permite decidir de forma async si un click fuera debe cerrar. Esas reglas no las puede deducir Zag mirando un DialogRoot
			individual, especialmente porque los dialogs están en portales y pueden quedar varios montados a la vez.

			Cuando ambos intentan gobernar lo mismo aparecen carreras. Llegamos a esta decisión después de ver varios síntomas:
			pointer-events:none quedándose aplicado en el dialog equivocado, warnings de aria-hidden porque el foco seguía dentro de
			un elemento que Zag acababa de ocultar, errores de focus-trap al no encontrar ningún elemento enfocable y eventos outside
			tardíos de un dialog anterior intentando cerrar el siguiente. No es que Zag esté mal; es que le estábamos pidiendo
			compartir una autoridad que en esta arquitectura ya pertenece a ModalProvider.

			Por eso la frontera queda así: Chakra/Zag se usa como capa visual controlada (render, posición y lifecycle de salida);
			ModalProvider gobierna el modal real (stack, open/close, outside, foco inicial/restauración y resolución de promesas).
			Los flags en false no son parches sueltos: documentan que Zag no debe tomar decisiones de comportamiento modal aquí.

			Qué significa cada prop, algunas son de Zag https://zagjs.com/components/react/dialog:
			- open={modal.open}: el estado lo decide ModalProvider; DialogRoot sólo renderiza ese estado.
			- modal={false}: Zag no aplica comportamiento modal global, como aria-hidden, pointer blocking o su stack interno.
			- trapFocus={false}: Zag no instala focus-trap; evitamos errores cuando el contenido cambia o se desmonta durante cierres.
			- preventScroll={false}: Zag no bloquea el scroll del documento.
			- restoreFocus={false}: Zag no restaura el foco al cerrar; lo hacemos nosotros con ModalUtils.
			- closeOnInteractOutside={false}: Zag no cierra por clicks/taps fuera; sólo el Modal top escucha pointerdown y delega en ModalProvider.
			- closeOnEscape={false}: Zag no cierra por Escape; si lo necesitamos, debe añadirse como regla de ModalProvider.
			- onExitComplete: seguimos usando el lifecycle visual de Chakra/Zag para saber cuándo ya podemos quitar el modal del stack.
			- lazyMount={true}: el contenido no se monta hasta que el dialog se abre; mantenemos este comportamiento de render.
			- initialFocusEl: le decimos a Zag CUÁL es el elemento inicial en lugar de dejarle buscarlo.
				Aunque trapFocus esté en false, Zag ejecuta su getInitialFocus al abrir, y eso llama a
				getTabbables, que recorre todos los enfocables comprobando visibilidad con offsetWidth uno
				por uno. Medido en la transición mesas -> venta con la CPU a x6 (bench/journal.md): 84
				lecturas forzadas de layout, la mayoría de los ~120 recálculos de estilo de la transición.
				Dándole el contenido no enumera nada; el foco de verdad lo pone ModalUtils.sendInitialFocus,
				que es quien manda aquí igual que con el resto de decisiones modales.
		*/}
		<DialogRoot id={modal.modalId} open={modal.open} modal={false} trapFocus={false} preventScroll={false} restoreFocus={false} motionPreset={'none'} placement={placement} onExitComplete={onCloseComplete} size={rootSize} closeOnInteractOutside={false} lazyMount={true} closeOnEscape={false} initialFocusEl={() => contentRef.current}>

			{/* OJO: No se muestra backdrop porque todo se controla a través del WaitingOverlay */}
			<DialogContent data-fullscreen={modal.open && isFullScreen ? 'true' : undefined} shadow={'dark-lg'} ref={contentRef} {...contentSizeProps} overflow={'hidden'} backdrop={false} borderRadius={isFullScreen ? 'none' : 'lg'} border={isFullScreen ? 'none' : { base: 'none', _dark: '1px solid var(--chakra-colors-neutral-700)' }} bg='none'>
				<WaitingOverlayProvider>
					{
						/*
							HACK: Mientras se cierra el dialogo no muestro nada para que no se renderice. Es un dialogo que va a acabar cerrandose y va a invocar a onExitCompleted. Si segimos mostrando
							su contenido podemos correr el riesgo en el render de hacer referencia a contenidos que ya no existen. Por ejemplo, en la pantalla de cerrar jornada al cobrar el último ticket
							deja de existir, y si se vuelve hacer un render lanza un error de acceso a un objeto null. Tanto el close como los cancel del dialogo no son instantáneos, simplemente marcan
							el modal como not opened para que se cierre, cuando se cierra por completo es en onExitComplete. Aunque hicieramos async en el close y el cancel estaríamos en las mismas, estaríamos
							renderizando el contenido, el unmount del dialogo no es instantaneo.
						*/
					}
					<IsCoveredByOtherScreenContext.Provider value={isCoveredByOtherScreen}>
						{modal.open && modal.content}
					</IsCoveredByOtherScreenContext.Provider>
				</WaitingOverlayProvider>
			</DialogContent>
		</DialogRoot>
	</ModalContext.Provider>;
});

export const ModalProvider: React.FC<PropsWithChildren> = ({ children }) => {

	const [modals, setModals] = useState<Instance[]>([]);
	const [fullScreenModalIds, setFullScreenModalIds] = useState<ReadonlySet<string>>(() => new Set());
	const prevModalsRef = React.useRef<Instance[]>([]);


	const show = useCallback(<T,>(instance: Omit<Instance, 'resolve'>) => {
		return new Promise<Result<T>>((resolve) => {
			setModals((prevModals) => [
				...prevModals,
				{ ...instance, open: true, resolve } as Instance,
			]);
		});
	}, []);

	const close = useCallback((modalId: string, result: unknown) => {
		setModals(modals => {
			const isDeferredClose = modalId !== modals.last()?.modalId;
			return modals.map(modal => modal.modalId === modalId ? { ...modal, open: isDeferredClose, closeResult: Result.ok(result), deferredClose: isDeferredClose } : modal);
		});
	}, []);

	const cancel = useCallback((modalId: string) => {
		setModals(modals => {
			const isDeferredCancel = modalId !== modals.last()?.modalId;
			return modals.map(modal => modal.modalId === modalId ? { ...modal, open: isDeferredCancel, closeResult: Result.error(), deferredClose: isDeferredCancel } : modal);
		});
	}, []);

	// Se mantiene separado de modals para no reemplazar las instancias: el efecto de altas y bajas las compara por identidad.
	const setModalFullScreen = useCallback((modalId: string, isFullScreen: boolean) => {
		setFullScreenModalIds(currentIds => {
			if (currentIds.has(modalId) === isFullScreen)
				return currentIds;

			const updatedIds = new Set(currentIds);
			if (isFullScreen)
				updatedIds.add(modalId);
			else
				updatedIds.delete(modalId);

			return updatedIds;
		});
	}, []);

	const onExitComplete = useCallback((modalId: string) => {
		setModalFullScreen(modalId, false);
		setModals(modals => {

			const modal = modals.find(m => m.modalId === modalId);
			if (!modal)
				throw Error(`Se está intentando cerrar el modal con Id ${modalId} que no existe`);

			modal.resolve(modal.closeResult ?? Result.error());
			return modals.filter(m => m.modalId !== modalId);
		});
	}, [setModalFullScreen]);

	const setDismissHandler = useCallback((modalId: string, handler: DismissHandler) => {
		setModals(modals => modals.map(modal => modal.modalId === modalId ? { ...modal, dismissHandler: handler } : modal));
	}, []);

	React.useEffect(() => {
		const prevModals = prevModalsRef.current;
		const newModals = modals;

		const addedModals = newModals.except(prevModals);
		if (addedModals.length > 0) {
			// Establezco el foco incial en la nueva pantalla
			ModalUtils.sendInitialFocus(addedModals.last()?.modalId ?? 'unknown');
			void Events.raise('dialog:opened');
		}

		if (prevModals.except(newModals).length > 0) {
			// Restauro el foco al dialogo anterior cuando se cierra
			ModalUtils.sendFocus(modals.last()?.modalId ?? 'unknown');
			void Events.raise('dialog:closed');
		}

		// Guardo el estado actual para la próxima renderización
		prevModalsRef.current = modals;
	}, [modals]);

	// En nuestros flujos de pantallas en ocasiones puede ocurrir que algún evento cierre diálogos anteriores al que está en top (Por ejemplo con el evento ticket:closed cuando
	// lo lanza el SalesStore). Para evitar que se cierren en un orden inadecuado programamos un deferredClose, de esta manera cuando se cierra el padre, posteriormente se cierra el dialogo
	// en el orden que toca. Esto evita problemas con el WaitingOverlay y posibles errores inesperados que nos podamos encontrar.
	React.useEffect(() => {
		// Si el siguiente modal tiene un deferredClose. Lo cierro con el resultado que ya tenía
		const topModal = modals.last();
		if (topModal && topModal.deferredClose)
			setModals(modals => modals.map(modal => modal.modalId === topModal.modalId ? { ...modal, open: false, deferredClose: false } : modal));
	}, [modals]);

	const context = React.useMemo(() => ({ show, close, cancel, setDismissHandler }), [show, close, cancel, setDismissHandler]);

	return (
		<Context.Provider value={context}>
			{children}
			{
				modals.map((modal, index) => {
					const isCoveredByOtherScreen = modals.slice(index + 1).some(otherModal => otherModal.open && fullScreenModalIds.has(otherModal.modalId));

					return <Modal key={modal.modalId} modal={modal} onExitComplete={onExitComplete} cancel={cancel} isTop={modals.last()?.modalId === modal.modalId} isFullScreen={fullScreenModalIds.has(modal.modalId)} isCoveredByOtherScreen={isCoveredByOtherScreen} setDismissHandler={setDismissHandler} onFullScreenChange={setModalFullScreen} />;
				})
			}
		</Context.Provider>
	);
};


export type Actions<TResult = unknown> = { close: (result: TResult) => void, cancel: () => void };

type ShowFunction = <C extends React.ComponentType<any>, P extends React.ComponentProps<C>, T = P extends Actions<infer U> ? U : never>(
	Screen: C,
	props?: Omit<P, keyof Actions<T>> & Omit<Options, 'content'>
) => Promise<Result<T>>;

function useShow(): ShowFunction {

	const builder = useBuilder();

	const showModal: ShowFunction = async function <
		TModalComponent extends React.ComponentType<any>,
		TModalComponentProps extends React.ComponentProps<TModalComponent>,
		TModalComponentResult = TModalComponentProps extends Actions<infer TResult> ? TResult : never
	>(Screen: TModalComponent, otherProps: Omit<TModalComponentProps, keyof Actions<TModalComponentResult>> & Omit<Options, 'content'> = {} as any): Promise<Result<TModalComponentResult>> {

		const { show: showModal, close: closeModal, cancel: cancelModal } = builder();
		const { placement, size, closeOnInteractOutside, ...props } = otherProps;
		const result = await showModal<TModalComponentResult>({
			content: <Screen {...props as any} cancel={cancelModal} close={closeModal} />,
			placement,
			closeOnInteractOutside,
			size
		});
		return result;
	};

	return showModal;
}

let showFn: ShowFunction | null = null;

export function Manager({ children, onReady }: React.PropsWithChildren & { onReady: () => void }) {

	const showScreen = useShow();

	const hasRun = React.useRef(false);
	React.useEffect(() => {
		if (hasRun.current)
			return;
		hasRun.current = true;
		showFn = showScreen;
		onReady();
	}, [onReady, showScreen]);

	return children;
}

export const show: ShowFunction = (Modal, props = {} as any) => {
	if (!showFn)
		throw new Error('ModalManager has not been mounted. You have to use an ScreenManager to be able to show screens.');

	return showFn(Modal, props);
};
